// Per-page SEO tags for the single-page app. Firebase Hosting serves the same
// index.html for every URL, so the title, description, canonical URL and robots
// directive of a result page (?s=word&hl=lang&t=type) are set here once the
// search has finished; Google indexes the rendered page.
//
// - Result pages with results: descriptive title/description, a canonical URL
//   on double-rhyme.com (doppel-reim.de serves the same site), indexable.
// - Empty results, errors, over-long terms: `noindex` so thin "No rhymes
//   found" pages stay out of the index (crawlers request many such URLs).
// - Home (no search term, with or without ?hl=): canonical is the root.
import languages from './constants';
import {DEFAULT_RHYME_TYPE, getRhymeType} from './rhymeTypes';

export const SITE_ORIGIN = 'https://double-rhyme.com';
export const SITE_NAME = 'Double Rhyme';
export const HOME_TITLE = 'Double Rhyme – rhyme finder for multi-syllable rhymes';
export const HOME_DESCRIPTION =
    'Double Rhyme (Doppelreim) finds multi-syllable double rhymes, vowel rhymes, classic rhymes ' +
    'and proverbs in 50+ languages. Free rhyme dictionary for rap lyrics, songs and poems.';

// What the result page lists, used in its title ("Double rhymes for ...").
const TYPE_NOUNS = {
    double: 'Double rhymes',
    vowel: 'Vowel rhymes',
    classic: 'Rhymes',
    proverb: 'Proverbs and lines',
};

// Language ids of the API -> BCP 47 tags for <html lang>.
const HTML_LANG = {es_lat: 'es-419', fr_be: 'fr-BE', fr_ch: 'fr-CH', pt_br: 'pt-BR', hy_arevmda: 'hyw'};

export function languageName(code) {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code;
}

export function canonicalUrl(word, language, rhymeType) {
    const term = (word || '').trim();
    if (!term) {
        return `${SITE_ORIGIN}/`;
    }
    // Same parameter order as the sitemap (?s=...&hl=...); the type only when
    // it is not the default, so existing double-rhyme URLs stay canonical.
    const params = new URLSearchParams();
    params.set('s', term);
    params.set('hl', language);
    if (rhymeType && rhymeType !== DEFAULT_RHYME_TYPE) {
        params.set('t', rhymeType);
    }
    return `${SITE_ORIGIN}/?${params.toString()}`;
}

// Pure: the tags a page should carry. `state` is one of
// 'home' | 'loading' | 'results' | 'empty' | 'error'.
export function pageMeta({word, language, rhymeType, state, sample = []}) {
    const term = (word || '').trim();
    if (state === 'home' || !term) {
        return {title: HOME_TITLE, description: HOME_DESCRIPTION, canonical: `${SITE_ORIGIN}/`, robots: null, lang: 'en'};
    }
    const langName = languageName(language);
    const noun = TYPE_NOUNS[getRhymeType(rhymeType).id] || 'Rhymes';
    const title = `${noun} for "${term}" (${langName}) – ${SITE_NAME}`;
    // Buckets overlap, so the same word can occur in several of them.
    const words = [...new Set(sample.filter(Boolean))].slice(0, getRhymeType(rhymeType).wide ? 2 : 10).join(', ');
    const description = words
        ? `${noun} for "${term}" (${langName}): ${words}. Find multi-syllable rhymes for rap lyrics and poems with ${SITE_NAME}.`
        : `${noun} for "${term}" (${langName}). Find multi-syllable rhymes for rap lyrics and poems with ${SITE_NAME}.`;
    return {
        title,
        description: description.length > 300 ? `${description.slice(0, 297)}...` : description,
        canonical: canonicalUrl(term, language, rhymeType),
        // A finished search without results is a thin page: keep it out of the
        // index. Never noindex while loading -- a slow API response must not
        // get a good page dropped by a crawler that renders too early.
        robots: state === 'empty' || state === 'error' ? 'noindex, follow' : null,
        lang: HTML_LANG[language] || language.replace(/_/g, '-'),
    };
}

function upsert(selector, create) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = create();
        document.head.appendChild(el);
    }
    return el;
}

export function applyMeta(meta) {
    document.title = meta.title;
    document.documentElement.lang = meta.lang || 'en';
    upsert('meta[name="description"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'description');
        return m;
    }).setAttribute('content', meta.description);
    upsert('link[rel="canonical"]', () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
    }).setAttribute('href', meta.canonical);
    const robots = document.head.querySelector('meta[name="robots"]');
    if (meta.robots) {
        upsert('meta[name="robots"]', () => {
            const m = document.createElement('meta');
            m.setAttribute('name', 'robots');
            return m;
        }).setAttribute('content', meta.robots);
    } else if (robots) {
        robots.remove();
    }
}
